import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/AddUnit.css";

const RoomAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roomName, setRoomName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [roomId, setRoomId] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    // Check if we're in edit mode
    if (location.state && location.state.room && location.state.isEditing) {
      const { room } = location.state;
      setRoomName(room.room_no);
      setRoomId(room.id);
      setIsEditing(true);
    } else {
      // Reset to add mode if no edit state
      setRoomName("");
      setRoomId(null);
      setIsEditing(false);
    }
  }, [location.state]);

  // Cleanup effect to reset state when component unmounts or location changes
  useEffect(() => {
    return () => {
      setRoomName("");
      setRoomId(null);
      setIsEditing(false);
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roomName.trim()) {
      showToast("Please enter a room name or number", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = isEditing ? "room-edit.php" : "room-add.php";
      const body = isEditing 
        ? { id: roomId, room_no: roomName }
        : { room_no: roomName };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.status === "success") {
        const action = isEditing ? "updated" : "added";
        showToast(`Room "${roomName}" ${action} successfully!`, "success");
        setRoomName("");
        setTimeout(() => {
          navigate("/room-list");
        }, 1500);
      } else {
        showToast(data.message || `Failed to ${isEditing ? 'update' : 'add'} room`, "error");
      }
    } catch (error) {
      showToast(`Network error: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>{isEditing ? "Edit Room" : "Add New Room"}</h1>
        <p>{isEditing ? "Update room information" : "Create a new room for booking or assignment"}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="roomName">Room Name / Number</label>
            <input
              type="text"
              id="roomName"
              name="roomName"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="e.g., Conference Room 1, B-102"
              required
              disabled={isSubmitting}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting 
              ? (isEditing ? "Updating Room..." : "Adding Room...") 
              : (isEditing ? "Update Room" : "Add Room")
            }
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomAdd;
